/**
 * Login page demo controller — /auth/login（src/pages/auth/login.html）
 *
 * Demo-only 行為（不驗帳密、不打後端）：
 *   1. 雙 tab 切換：點「診所登入」/「會員登入」切 is-active + aria-selected（視覺高亮跟著換）。
 *      tablist 用 roving tabindex（active tab tabindex=0、其餘 -1）+ ←/→ 鍵盤切換，符合 WAI-ARIA tabs。
 *   2. 按登入直通後台（DEMO BYPASS 2026-06-18，無後端、接真 auth 時還原）：攔截 #login-form submit →
 *      preventDefault → 依當前 active tab 的 data-login-target 跳轉
 *      （診所→../admin/dashboard.html、會員→../member/dashboard.html）。
 *      不驗帳密——帳號 / 密碼填什麼或留空都直接跳（form 已 novalidate，不擋空白）。
 *
 * 跳轉目標是 data-driven：從 active tab button 的 data-login-target 讀，不在 JS hardcode 路徑。
 *   data-login-target 用「相對當前頁」路徑（../admin/dashboard.html）：本站部署於子路徑
 *   （demo.nsdi.com.tw/2026-medproof/dist/），window.location.href 由瀏覽器相對 auth/login.html 解析，
 *   子路徑下正確。勿改回絕對 /admin/...（會解析到網域根而 404；且 vite postprocess 不改寫 data-* 內路徑）。
 *
 * 設計：root 守門早退——頁面沒有 #login-form 就 return，非該頁不執行、不報錯。
 *   絕不碰 components/auth-forms.js（那是 backend 的註冊 / 忘記密碼邏輯）。
 */

function init() {
  const form = document.getElementById('login-form');
  // root 守門：非登入頁（無 #login-form）直接早退，整支不執行。
  if (!form) return;

  const tablist = document.querySelector('.auth-card__tabs[role="tablist"]');
  const tabs = tablist
    ? Array.from(tablist.querySelectorAll('[role="tab"]'))
    : [];

  /** 取當前 active tab（以 is-active class 為準，fallback 第一顆）。 */
  function activeTab() {
    return tabs.find((t) => t.classList.contains('is-active')) || tabs[0] || null;
  }

  /** 切換選定 tab：更新 is-active / aria-selected / roving tabindex。 */
  function selectTab(target) {
    if (!target) return;
    tabs.forEach((tab) => {
      const on = tab === target;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      // roving tabindex：只有 active tab 可 Tab 進入，其餘用 ←/→ 巡覽。
      tab.tabIndex = on ? 0 : -1;
    });
  }

  /**
   * 由 URL ?tab= 決定初始 active tab（nav 兩顆 CTA 帶 member / clinic 進來）。
   *   ?tab=member → 會員登入（#tab-member）；?tab=clinic → 診所登入（#tab-clinic）。
   *   無參數或亂值 → fallback 會員登入（用戶要會員優先），不報錯。
   * 對齊 tab button 的 id（tab-<value>），保持 data-driven、不在 JS hardcode 路徑/順序。
   */
  function tabFromQuery() {
    if (!tabs.length) return null;
    let value = '';
    try {
      value = new URLSearchParams(window.location.search).get('tab') || '';
    } catch (_) {
      value = '';
    }
    const byId = value && tabs.find((t) => t.id === `tab-${value}`);
    // 無參數 / 亂值 → 會員優先（id=tab-member），再不行才退第一顆。
    return byId || tabs.find((t) => t.id === 'tab-member') || tabs[0] || null;
  }

  // 初始化：依 ?tab= 設 active tab（覆蓋 HTML 預設）+ 同步 roving tabindex。
  if (tabs.length) selectTab(tabFromQuery());

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => selectTab(tab));

    // 鍵盤：←/→ 切換並聚焦（Home/End 跳首尾），符合 WAI-ARIA tablist 慣例。
    tab.addEventListener('keydown', (event) => {
      let nextIndex = null;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = tabs.length - 1;
      if (nextIndex === null) return;
      event.preventDefault();
      const next = tabs[nextIndex];
      selectTab(next);
      next.focus();
    });
  });

  // 按登入直通：攔截 submit，依 active tab 的 data-login-target 跳轉（不驗帳密）。
  // TODO(backend): 登入改打 POST /auth/login 驗帳密建 session，移除 DEMO BYPASS 直通 — 見 docs/BACKEND_HANDOFF.md#auth-login
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const target = activeTab();
    const dest = target && target.getAttribute('data-login-target');
    if (dest) window.location.href = dest;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
